import { IconButton, Paper, Typography } from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import PopoverDefault from "components/PopoverDefault"
import FileDropCell from "components/FileUplaod/FileDropCell"
import { CloseRounded, ImageRounded } from "resource/muiImport"

const IMAGE_EXT = /\.(jpg|jpeg|png|gif|bmp|webp)$/i

/**
 * A DataGrid cell that both SHOWS a row's attachments and accepts new ones: an image
 * icon opening a popover of thumbnails (each deletable, click to enlarge), next to a
 * drop zone that uploads straight from a drag or a click.
 *
 * Extracted from Menu 1 PettyCash, which is still its main user — Menu 7.7's per-vehicle
 * certificates need exactly the same cell, so it lives here rather than being copied.
 *
 * The row's file array is the column's value: each entry needs a `Url`, and a name under
 * any of FileName / Name / Label / FileNm.
 *
 * @param uploadApiNm         endpoint the drop zone posts to
 * @param formDataKey         name of the id field that endpoint expects
 * @param getRefId            row -> that id. Defaults to the grid row id, which is right
 *                            when the row IS the record (PettyCash). Pass it when the
 *                            grid is keyed on something else — Menu 7.7 keys rows by
 *                            index because a new line has no id until it is saved.
 * @param isDisabled          row -> true to refuse uploads
 * @param onDeferFile         (params, file) -> hold this file until the record is saved.
 *                            Without it a row with no id shows "Drop disabled"; with it
 *                            the zone stays live and the caller uploads after saving.
 *                            Called with null to drop the held file.
 * @param getPendingFile      row -> the File being held, so the cell can show its name
 * @param handleDeleteFile    file -> delete it (confirm inside)
 * @param onFileUploadSuccess called after a successful upload, to refresh
 * @param onImageClick        url -> open full screen; falls back to a new tab
 */
export const renderUploadCell = ({
  uploadApiNm, formDataKey, handleDeleteFile, onFileUploadSuccess, onImageClick,
  getRefId = (params) => params.id, isDisabled = () => false,
  onDeferFile, getPendingFile = () => null,
}) => (params) => {

  // id 0 is the pinned sum row — it owns no files.
  if (params.id === 0) return null

  const refId = getRefId(params)
  const files = params.value || []
  const pending = getPendingFile(params)

  return (
    <BoxFR>
      {files.length > 0 &&
        <PopoverDefault icon={<ImageRounded color="primary" />} >
          <Paper sx={{ p: 1 }}>
            <BoxFC sx={{ gap: 1 }}>
              {files.map((file, index) => (
                <BoxFR key={index} sx={{ justifyContent: 'center', position: 'relative' }}>
                  {file.Url && IMAGE_EXT.test(file.Url) ? (
                    <img
                      src={file.Url}
                      alt={`Preview ${index + 1}`}
                      style={{
                        maxWidth: '150px',
                        maxHeight: '100px',
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease-in-out',
                      }}
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                      onClick={() => onImageClick ? onImageClick(file.Url) : window.open(file.Url, "_blank")}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        width: '100%',
                        p: 1,
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        backgroundColor: '#f5f5f5',
                        '&:hover': { backgroundColor: '#e0e0e0' },
                        maxWidth: '200px',
                        wordBreak: 'break-word',
                      }}
                      onClick={() => window.open(file.Url, "_blank")}
                    >
                      📄 {file.FileName || file.Name || file.Label || file.FileNm || `File ${index + 1}`}
                    </Typography>
                  )}

                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                      minWidth: '20px',
                      width: '20px',
                      height: '20px',
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteFile(file)
                    }}
                  >
                    <CloseRounded />
                  </IconButton>
                </BoxFR>
              ))}
            </BoxFC>
          </Paper>
        </PopoverDefault>
      }
      {pending &&
        <BoxFR sx={{ gap: 0 }}>
          <Typography variant="caption" noWrap sx={{ maxWidth: 70 }} title={pending.name}>
            📎 {pending.name}
          </Typography>
          <IconButton size="small" sx={{ p: 0 }} onClick={() => onDeferFile(params, null)}>
            <CloseRounded fontSize="small" />
          </IconButton>
        </BoxFR>
      }
      {!pending &&
        <FileDropCell
          formDataObj={{ [formDataKey]: refId }}
          uploadApiNm={uploadApiNm}
          onUploadSuccess={onFileUploadSuccess}
          colNm={params.colDef.field}
          hasValue={files.length > 0}
          disabled={isDisabled(params) || (!refId && !onDeferFile)}
          onFileSelect={!refId && onDeferFile ? (file) => onDeferFile(params, file) : undefined}
        />
      }
    </BoxFR>
  )
}

export default renderUploadCell
